import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { CopyButton } from "@pages/AdminDashboard/ui/CopyButton";
import { ExternalLink } from "@pages/AdminDashboard/ui/DataTable/cells";
import { FieldList, FieldListItem } from "@pages/AdminDashboard/ui/FieldList";
import { StatusBadge, Tag } from "@pages/AdminDashboard/ui/StatusBadge";
import { telehealthPatientUrl } from "@pages/AdminDashboard/telehealthLinks";
import { AdminUserProfile } from "@types-runway/admin";

import { formatUserProfile } from "./formatUserProfile";

const { color, radius, statusTone, typeScale } = adminTokens;
const [, spaceSm] = adminTokens.spacing;
const stripeCustomerPrefix = "cus_";
const stripeCustomerPage = "https://dashboard.stripe.com/customers/";
const noValue = "—";

const ValueRow = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  max-width: 100%;
`;

const ClinicRow = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spaceSm}px;
`;

// The Done look of BADGE-R3…R8 with a check: the clinic profile exists (USER-R31).
const CreatedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  height: 22px;
  padding: 0 10px;
  border: 1px solid ${statusTone.done.border};
  border-radius: ${radius.pill}px;
  background-color: ${statusTone.done.background};
  color: ${statusTone.done.text};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: 12px;
  white-space: nowrap;
`;

const PatientId = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const ClinicProfile = ({ patientId }: { patientId?: string | null }) => {
  if (!patientId) return <StatusBadge status="Not created" />;

  return (
    <ClinicRow>
      <CreatedBadge>
        <CheckIcon />
        Created
      </CreatedBadge>
      <ValueRow>
        <PatientId>{patientId}</PatientId>
        <CopyButton value={patientId} what="patient ID" />
      </ValueRow>
      <ExternalLink href={telehealthPatientUrl(patientId)}>Open clinic profile</ExternalLink>
    </ClinicRow>
  );
};

const Billing = ({ customerId }: { customerId?: string | null }) => {
  if (!customerId?.startsWith(stripeCustomerPrefix)) return <>{noValue}</>;

  return (
    <ExternalLink href={`${stripeCustomerPage}${customerId}`}>Open billing profile</ExternalLink>
  );
};

// A value with a copy Button at its right; a field with no value has none (USER-R33).
const copyable = (value: string, what: string) =>
  value === noValue ? (
    value
  ) : (
    <ValueRow>
      {value}
      <CopyButton value={value} what={what} />
    </ValueRow>
  );

const copyNames: Record<string, string> = { Email: "email", Phone: "phone" };

interface UserProfileCardProps {
  profile: AdminUserProfile;
}

// The name once as the card title, then Email, Phone, Date of birth, State, Admin, Clinic profile
// and Billing (USER-R11, USER-R12, USER-R31…R33, USER-R38…R40).
const UserProfileCard = ({ profile }: UserProfileCardProps) => {
  const [name, ...textFields] = formatUserProfile(profile);
  const adminMark = profile.isAdmin ? <Tag>Admin</Tag> : <StatusBadge status="Not an admin" />;
  const fields: FieldListItem[] = [
    ...textFields.map(({ label, value }) => ({
      label,
      value: copyNames[label] ? copyable(value, copyNames[label]) : value,
    })),
    { label: "Clinic profile", value: <ClinicProfile patientId={profile["patient-id"]} /> },
    { label: "Billing", value: <Billing customerId={profile.customer_id} /> },
  ];

  return (
    <div data-testid="user-profile-card">
      <Card eyebrow="User" title={name.value} aside={adminMark}>
        <FieldList fields={fields} />
      </Card>
    </div>
  );
};

export default UserProfileCard;
